import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { FaFileInvoice, FaSearch } from 'react-icons/fa';
import CustomModal from '../CustomModal/CustomModal';
import {
  EstimateEquipment,
  EstimateProduct,
  EstimateService,
  EstimateViewModel,
} from '../../models/estimate/EstimateViewModel';
import api from '../../services/api';
import { useAuth } from '../../contexts/auth';
import {
  InvoiceProduct,
  InvoiceService,
  InvoiceViewModel,
} from '../../models/Invoice/InvoiceViewModel';
import CustomButton from '../CustomButton/CustomButton';
import { toMask } from '../../services/currency';
import { getRandom } from '../../services/utils';
import InvoiceStatusState from '../../enums/InvoiceStatusState';
import InvoicesList from '../../pages/invoices/InvoicesList';
import { InvoiceFilterFields } from '../../pages/invoices/InvoicesFilter';

interface EstimateFooProps {
  estimate: EstimateViewModel;
  isOpen: boolean;
  setIsOpenModal: (isOpen: boolean) => void;
}

export default function EstimateFoo({
  estimate,
  isOpen,
  setIsOpenModal,
}: EstimateFooProps) {
  const {
    user: { organization },
  } = useAuth();

  const [isInvoicesOpenModal, setInvoicesIsOpenModal] = useState(false);
  const [invoicesSelected, setInvoicesSelected] = useState<number[]>([]);
  const [isInvoicesLoading, setIsInvoicesLoading] = useState(false);
  const [isInvoicesToAddLoading, setIsInvoicesToAddLoading] = useState(false);
  const [invoices, setInvoices] = useState<InvoiceViewModel[]>([]);
  const [invoicesToAdd, setInvoicesToAdd] = useState([] as InvoiceViewModel[]);
  const [estimateServices, setEstimateServices] = useState<EstimateService[]>(
    []
  );
  const [estimateProducts, setEstimateProducts] = useState<EstimateProduct[]>(
    []
  );
  const [estimateEquipments, setEstimateEquipments] = useState<
    EstimateEquipment[]
  >([]);
  const [selectedInvoice, setSelectedInvoice] =
    useState<InvoiceViewModel | null>(null);

  const getInvoices = async () => {
    setIsInvoicesLoading(true);
    const { data } = await api.get(
      `organizations/${organization.id}/estimates/${estimate.id}/invoices`
    );
    setInvoices(data.data);
    setIsInvoicesLoading(false);
  };

  const onEstimateServiceChecked = (estimateService: EstimateService): void => {
    estimateService.toggleChecked();
  };

  const onEstimateProductChecked = (estimateProduct: EstimateProduct): void => {
    estimateProduct.toggleChecked();
  };

  const onEstimateEquipmentChecked = (
    estimateEquipment: EstimateEquipment
  ): void => {
    estimateEquipment.toggleChecked();
  };

  const getInvoice = async (id: number) => {
    if (id !== selectedInvoice?.id) {
      const { data: invoice } = await api.get(
        `organizations/${organization.id}/invoices/${id}`
      );
      setSelectedInvoice(invoice);
    }
  };

  const searchInvoice = async () => {
    if (organization) {
      setIsInvoicesToAddLoading(true);
      const filter = new InvoiceFilterFields({
        customer: estimate.customerId,
        company: estimate.companyId,
        transactionNature: estimate.transactionNatureId,
        withoutEstimate: true,
      });
      const response = await api.get(
        `organizations/${organization.id}/invoices/filter`,
        {
          params: filter,
        }
      );
      setInvoicesToAdd(response.data.data);
      setIsInvoicesToAddLoading(false);
      setInvoicesIsOpenModal(true);
    }
  };

  const addToEstimate = async () => {
    if (
      // eslint-disable-next-line no-restricted-globals
      confirm(
        'Você deseja adicionar a(s) Ordem(ns) de Serviço ao Orçamento? Essa ação não poderá ser desfeita'
      )
    ) {
      try {
        const promises: any[] = [];
        const ids = invoicesSelected;
        ids.forEach((id) => {
          promises.push(
            api.post(
              `organizations/${organization.id}/estimates/${estimate.id}/invoices/${id}`
            )
          );
        });

        await Promise.all(promises);
        setInvoicesIsOpenModal(false);
        getInvoices();
      } catch (e: any) {
        toast.error(
          e?.response?.data?.errors.reduce(
            (acc: string, error: string) => acc + error,
            ''
          )
        );
      }
    }
  };

  const generateInvoice = async () => {
    if (
      // eslint-disable-next-line no-restricted-globals
      confirm(
        'Você deseja adicionar uma nova Ordem de Serviço ao Orçamento? Essa ação não poderá ser desfeita'
      )
    ) {
      const { data: invoice } = await api.post(
        `organizations/${organization.id}/estimates/${estimate.id}/invoices`
      );
      setInvoices([...invoices, invoice]);
    }
  };

  const addToSelectedInvoice = async () => {
    if (!selectedInvoice?.id) {
      toast.info('Selecione um ordem de serviço');
      return;
    }
    const estimateEquipmentsSelected = estimateEquipments.filter(
      (e) => e.checked
    );
    const estimateServicesSelected = estimateServices.filter((e) => e.checked);
    const estimateProductsSelected = estimateProducts.filter((e) => e.checked);

    try {
      /* eslint-disable no-restricted-syntax */
      /* eslint-disable no-await-in-loop */
      for (const estimateEquipment of estimateEquipmentsSelected) {
        await api.post(
          `organizations/${organization.id}/invoices/${selectedInvoice?.id}/equipments`,
          {
            equipment: estimateEquipment.equipmentId,
          }
        );
      }

      for (const estimateService of estimateServicesSelected) {
        await api.post(
          `organizations/${organization.id}/invoices/${selectedInvoice?.id}/services`,
          {
            service: estimateService.serviceId,
            quantity: estimateService.isGrouped ? estimateService.quantity : 1,
            description: estimateService.description,
          }
        );
      }

      for (const estimateProduct of estimateProductsSelected) {
        await api.post(
          `organizations/${organization.id}/invoices/${selectedInvoice?.id}/products`,
          {
            product: estimateProduct.productId,
            quantity: estimateProduct.isGrouped ? estimateProduct.quantity : 1,
          }
        );
      }

      getInvoices();
    } catch (e: any) {
      if (e?.response?.data?.errors) {
        toast.error(
          e?.response?.data?.errors.reduce(
            (acc: string, error: string) => acc + error,
            ''
          )
        );
      } else {
        throw e;
      }
    }
  };

  const buildServiceList = (invoicesAvailable: InvoiceViewModel[]) => {
    const services: EstimateService[] = [];
    const invoiceServices = invoicesAvailable.reduce(
      (a: InvoiceService[], b) => a.concat(b.invoiceServices),
      []
    );
    estimate.estimateServices.forEach((s) => {
      /* eslint-disable no-plusplus */
      const servicesQuantity = invoiceServices
        .filter((i) => i.serviceId === s.serviceId)
        .reduce((a, b) => a + b.quantity, 0);
      const quantityToAdd = s.quantity - servicesQuantity;
      if (quantityToAdd === 0) return;
      if (s.isGrouped) {
        services.push(s);
      } else {
        for (let index = 0; index < quantityToAdd; index++) {
          const itemNumber = getRandom(index);
          const item = new EstimateService(s);
          item.itemNumber = itemNumber;
          services.push(item);
        }
      }
    });

    setEstimateServices(services);
  };

  const buildProductList = (invoicesAvailable: InvoiceViewModel[]) => {
    const products: EstimateProduct[] = [];
    const invoiceProducts = invoicesAvailable.reduce(
      (a: InvoiceProduct[], b) => a.concat(b.invoiceProducts),
      []
    );

    estimate.estimateProducts.forEach((p: EstimateProduct) => {
      const productsQuantity = invoiceProducts
        .filter((i) => i.productId === p.productId)
        .reduce((a, b) => a + b.quantity, 0);
      /* eslint-disable no-plusplus */
      const quantityToAdd = p.quantity - productsQuantity;
      if (quantityToAdd === 0) return;
      if (p.isGrouped) {
        products.push(p);
      } else {
        for (let index = 0; index < quantityToAdd; index++) {
          const itemNumber = getRandom(index);
          const item = new EstimateProduct(p);
          item.itemNumber = itemNumber;
          products.push(item);
        }
      }
    });
    setEstimateProducts(products);
  };

  useEffect(() => {
    getInvoices();
  }, [estimate]);

  useEffect(() => {
    const invoicesAvailable = invoices.filter(
      (i) => i.statusState !== InvoiceStatusState.Canceled
    );
    buildServiceList(invoicesAvailable);
    buildProductList(invoicesAvailable);

    const equipments: EstimateEquipment[] = [];
    estimate.estimateEquipments.forEach((e) => {
      if (
        !invoicesAvailable.some((i) =>
          i.invoiceEquipments.some((ie) => ie.equipmentId === e.equipmentId)
        )
      ) {
        const item = new EstimateEquipment(e);
        equipments.push(item);
      }
    });

    setEstimateEquipments(equipments);
  }, [invoices]);

  return (
    <>
      <CustomModal
        modalTitle='Incluir ordens de serviço'
        isOpen={isOpen}
        isLoading={isInvoicesLoading}
        setIsOpenModal={setIsOpenModal}
      >
        <h4>Ordens de Serviço</h4>
        <div className='d-flex align-items-center gap-2'>
          {invoices.map((invoice, index) => {
            return invoice.statusState === InvoiceStatusState.Canceled ||
              invoice.statusState === InvoiceStatusState.Completed ? (
              <span
                key={invoice.id}
                className={`btn ${
                  invoice.statusState === InvoiceStatusState.Canceled
                    ? 'btn-outline-secondary'
                    : 'btn-outline-success'
                } disabled`}
              >
                <FaFileInvoice /> {index + 1}
              </span>
            ) : (
              <CustomButton
                type='button'
                key={invoice.id}
                className={`btn ${
                  selectedInvoice?.id === invoice.id
                    ? 'btn-primary'
                    : 'btn-outline-primary'
                }`}
                handleClick={() => getInvoice(invoice.id)}
              >
                <FaFileInvoice /> {index + 1}
              </CustomButton>
            );
          })}
          <CustomButton
            className='btn btn-success'
            type='button'
            handleClick={generateInvoice}
          >
            Nova +
          </CustomButton>
          <CustomButton
            className='btn btn-primary'
            type='button'
            handleClick={searchInvoice}
          >
            Buscar <FaSearch />
          </CustomButton>
        </div>

        <hr />
        <div className='mb-3'>
          <span className='h5'>Equipamentos</span>
          <div className='table-responsive'>
            <table className='table'>
              <thead>
                <tr>
                  <th style={{ width: '20px' }}>&nbsp;</th>
                  <th>Nome</th>
                  <th>Marca</th>
                  <th>Modelo</th>
                  <th>N/S</th>
                  <th>Observações</th>
                </tr>
              </thead>
              <tbody>
                {estimateEquipments.length > 0 ? (
                  estimateEquipments.map((item) => (
                    <tr key={item.id}>
                      <td>
                        <input
                          className='form-check-input'
                          type='checkbox'
                          value={item.id}
                          defaultChecked={item.checked}
                          onClick={() => onEstimateEquipmentChecked(item)}
                        />
                      </td>
                      <td>{item.name}</td>
                      <td>{item.brand}</td>
                      <td>{item.model}</td>
                      <td>{item.serialNumber}</td>
                      <td>{item.description}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6}>Nenhum registro</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        <div className='mb-3'>
          <span className='h5'>Serviços</span>
          <div className='table-responsive'>
            <table className='table'>
              <thead>
                <tr>
                  <th style={{ width: '20px' }}>&nbsp;</th>
                  <th>Nome</th>
                  <th>Descrição</th>
                  <th>Quantidade</th>
                  <th className='text-end'>Valor</th>
                  <th className='text-end'>Total</th>
                </tr>
              </thead>
              <tbody>
                {estimateServices.length > 0 ? (
                  estimateServices.map((item) => (
                    <tr key={item.itemNumber}>
                      <td>
                        <input
                          className='form-check-input'
                          type='checkbox'
                          value={item.id}
                          defaultChecked={item.checked}
                          onClick={() => onEstimateServiceChecked(item)}
                        />
                      </td>
                      <td>{item.name}</td>
                      <td>{item.description ?? 'Sem descrição'}</td>
                      <td>{item.isGrouped ? item.quantity : 1}</td>
                      <td className='text-end'>{toMask(item.price)}</td>
                      <td className='text-end'>
                        {toMask(
                          (item.isGrouped ? item.quantity : 1) * item.price
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5}>Nenhum registro</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        <div className='mb-3'>
          <span className='h2'>Produtos</span>
          <div className='table-responsive'>
            <table className='table'>
              <thead>
                <tr>
                  <th style={{ width: '20px' }}>&nbsp;</th>
                  <th>Nome</th>
                  <th>Quantidade</th>
                  <th className='text-end'>Valor</th>
                  <th className='text-end'>Total</th>
                </tr>
              </thead>
              <tbody>
                {estimateProducts.length > 0 ? (
                  estimateProducts.map((item) => (
                    <tr key={item.itemNumber}>
                      <td>
                        <input
                          className='form-check-input'
                          type='checkbox'
                          value={item.id}
                          defaultChecked={item.checked}
                          onClick={() => onEstimateProductChecked(item)}
                        />
                      </td>
                      <td>{item.name}</td>
                      <td>{item.isGrouped ? item.quantity : 1}</td>
                      <td className='text-end'>{toMask(item.price)}</td>
                      <td className='text-end'>
                        {toMask(
                          (item.isGrouped ? item.quantity : 1) * item.price
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5}>Nenhum registro</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        <div className='mb-3'>
          <CustomButton
            type='button'
            className='btn btn-primary'
            handleClick={addToSelectedInvoice}
          >
            Adicionar à Ordem de Serviço Selecionada
          </CustomButton>
        </div>
      </CustomModal>
      <CustomModal
        modalTitle='Ordens de serviço'
        isOpen={isInvoicesOpenModal}
        isLoading={isInvoicesToAddLoading}
        setIsOpenModal={setInvoicesIsOpenModal}
      >
        <InvoicesList
          isMultiselect
          invoices={invoicesToAdd}
          setInvoicesSelected={setInvoicesSelected}
        />
        {invoicesToAdd.length > 0 && (
          <CustomButton
            type='button'
            className='btn btn-primary'
            handleClick={addToEstimate}
          >
            Adicionar ao Orçamento
          </CustomButton>
        )}
      </CustomModal>
    </>
  );
}
